import CalculatorContent from '@/components/DesignSystem/Subsections/Calculator/components/CalculatorContent';
import {
  CalculatorProvider,
  useCalculatorContext,
} from '@/components/DesignSystem/Subsections/Calculator/contexts';
import { useDynamicDropdowns } from '@/components/DesignSystem/Subsections/SalaryCalculator/utils/useDynamicDropdowns';
import { MobiusContext } from '@/components/MobiusProvider';
import {
  SALARY_CALCULATOR_CONFIG,
  DEGREE_LENGTH_CALCULATOR_CONFIG,
  CAREER_DETAILS_CONFIG,
} from '@/constants/calculatorConfigs';
import {
  Program,
  Occupation,
} from '@/components/DesignSystem/Subsections/SalaryCalculator/types';
import { MobiusTrackingContext } from '@/types/Mobius';
import { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { fetchPrograms, fetchOccupations } from './utils/calculatorApi';
import CalculatorWidget from '@/components/DesignSystem/Subsections/Calculator/components/CalculatorWidget';
import { newRelicNoticeError } from '@/utils/newRelicActions';
import getTrackingContext from '@/utils/getTrackingContext';

interface CalculatorProps {
  variant?: string;
}

const Calculator: React.FC<CalculatorProps> = ({
  variant = 'degree-length',
}) => {
  // Get options for DCS form and form state
  const { degrees, presets, siteConfig } = useContext(MobiusContext);
  const contextualDefaults = { ...presets };
  const identityToken = siteConfig?.identityToken;

  // State for programs and occupations
  const [programs, setPrograms] = useState<Program[]>([]);
  const [occupationObjects, setOccupationObjects] = useState<Occupation[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState<boolean>(false);
  const [loadingOccupations, setLoadingOccupations] = useState<boolean>(false);
  const [hasInitialFetch, setHasInitialFetch] = useState<boolean>(false);
  const [showCalculatorContent, setShowCalculatorContent] =
    useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [trackingContextLoaded, setTrackingContextLoaded] =
    useState<boolean>(false);
  // tracking context for the reccomended programs in the tools results
  const [trackingContext, setTrackingContext] = useState<
    Partial<MobiusTrackingContext>
  >({
    formatSubtype:
      variant === 'salary' ? 'salary-calculator-card' : 'program-length-card',
    formatType: 'dexp-tool-results',
  });

  const loadTrackingContext = async () => {
    try {
      const trackingTemp = trackingContext;
      const trackingContextWaitResult = await getTrackingContext(trackingTemp);
      setTrackingContext(trackingContextWaitResult);
      setTrackingContextLoaded(true);
    } catch (error) {
      // Handle any errors that occur during the asynchronous operation
      console.error('Error waiting for tracking context:', error);
      newRelicNoticeError(
        new Error(`Error waiting for tracking context: ${error}`),
      );
      setTrackingContextLoaded(true); // Set to true even on error to prevent blocking
    }
  };

  // get tracking context
  useEffect(() => {
    loadTrackingContext();
  }, []);

  // Use ref to avoid dependency issues with loading state
  const loadingProgramsRef = useRef<boolean>(false);

  // Keep ref in sync with state
  useEffect(() => {
    loadingProgramsRef.current = loadingPrograms;
  }, [loadingPrograms]);

  const initialValues = {
    degree: contextualDefaults.degree || 'bachelors',
    category: contextualDefaults.category,
    subject: contextualDefaults.subject,
  };

  const { formState, handleFieldChange } = useDynamicDropdowns(initialValues);

  // Fetch programs from mobius based on DCS selections - manual trigger only
  const handleFetchPrograms = useCallback(async () => {
    const selectedDegree = formState.current.degree;
    const selectedCategory = formState.current.category;
    const selectedSubject = formState.current.subject;

    // only get programs if all options are selected
    if (!selectedSubject || !selectedCategory || !selectedDegree) {
      // Clear programs if selections are not complete
      setPrograms([]);
      setOccupationObjects([]);
      return;
    }

    // Ensure form options are populated before attempting to fetch
    const hasOptions =
      formState.options?.degree?.length &&
      formState.options?.category?.length &&
      formState.options?.subject?.length;

    if (!hasOptions) {
      return;
    }

    // Wait for tracking context to be loaded before proceeding
    if (!trackingContextLoaded) {
      return;
    }

    // Prevent duplicate calls if already loading
    if (loadingProgramsRef.current) {
      return;
    }

    setLoadingPrograms(true);
    try {
      const result = await fetchPrograms({
        selectedDegree,
        selectedCategory,
        selectedSubject,
        trackingContext,
        formStateOptions: formState.options,
        identityToken,
      });

      setPrograms(result.programs);

      // Only fetch occupations if we have codes
      if (result.occupationCodes.length > 0) {
        setLoadingOccupations(true);
        try {
          const occupations = await fetchOccupations(result.occupationCodes);
          setOccupationObjects(occupations);
        } catch (error) {
          console.error('Error fetching occupations:', error);
          setOccupationObjects([]);
        } finally {
          setLoadingOccupations(false);
        }
      } else {
        setOccupationObjects([]);
      }
    } catch (error) {
      console.error('Error fetching Mobius programs:', error);
      setPrograms([]);
      setOccupationObjects([]);
    } finally {
      setLoadingPrograms(false);
      setHasInitialFetch(true); // Mark that we've completed the initial fetch
    }
  }, [
    formState.current.degree,
    formState.current.category,
    formState.current.subject,
    identityToken,
    formState.options,
    trackingContext,
    trackingContextLoaded,
  ]);

  // Manual trigger function to be passed to context
  const triggerDataFetch = useCallback(() => {
    setIsCalculating(true);
    handleFetchPrograms()
      .then(() => {
        // Add a small delay to show the loading state
        setTimeout(() => {
          setIsCalculating(false);
          setShowCalculatorContent(true); // Show calculator content when CTA is clicked
        }, 1000); // 1 second delay for better UX
      })
      .catch(() => {
        setIsCalculating(false);
      });
  }, [handleFetchPrograms]);

  // Initial fetch on component mount if all DCS selections are already set
  useEffect(() => {
    if (!formState?.current) return;

    const selectedDegree = formState.current.degree;
    const selectedCategory = formState.current.category;
    const selectedSubject = formState.current.subject;

    // Fetch on initial mount if all selections are available and we haven't fetched yet
    if (
      selectedDegree &&
      selectedCategory &&
      selectedSubject &&
      !hasInitialFetch
    ) {
      handleFetchPrograms();
    }
  }, [formState?.current, hasInitialFetch, handleFetchPrograms]); // Include hasInitialFetch to prevent multiple calls

  // Get the appropriate config based on variant
  const getEducationPanelConfig = (variant: string) => {
    switch (variant) {
      case 'salary':
        return SALARY_CALCULATOR_CONFIG;
      case 'degree-length':
        return DEGREE_LENGTH_CALCULATOR_CONFIG;
      case 'career-details':
        return CAREER_DETAILS_CONFIG;
      default:
        // Use salary calculator config as default but modify the name
        return {
          ...SALARY_CALCULATOR_CONFIG,
          id: `calculator-${variant}`,
          name: 'Education Selection',
          description: 'Configure your education details',
        };
    }
  };

  const educationPanelConfig = getEducationPanelConfig(variant || 'default');

  // Initialize calculator values from config's default values and current DCS selections
  const initialCalculatorValues = {
    // Include current DCS selections
    degree: formState.current.degree || '',
    category: formState.current.category || '',
    subject: formState.current.subject || '',
    // Include config default values
    ...(educationPanelConfig?.defaultValues || {}),
  };

  return (
    <CalculatorProvider
      formState={formState}
      handleFieldChange={handleFieldChange}
      variant={variant}
      config={educationPanelConfig}
      initialCalculatorValues={initialCalculatorValues}
      results={programs}
      occupations={occupationObjects}
      loadingPrograms={loadingPrograms}
      loadingOccupations={loadingOccupations}
      isCalculating={isCalculating}
      setIsCalculating={setIsCalculating}
      triggerDataFetch={triggerDataFetch}
      hasInitialFetch={hasInitialFetch}
    >
      {showCalculatorContent ? <CalculatorContent /> : <CalculatorWidget />}
    </CalculatorProvider>
  );
};

export default Calculator;
